import { useEffect, type ReactNode, type MouseEvent as ReactMouseEvent } from 'react';

type Props = {
	open: boolean;
	title: string;
	subtitle?: string;
	loading?: boolean;
	error?: string | null;
	onClose: () => void;
	children: ReactNode;
};

export default function AdminDrilldownModal({
	open,
	title,
	subtitle,
	loading = false,
	error,
	onClose,
	children
}: Props) {
	useEffect(() => {
		if (!open) return;
		document.body.classList.add('modal-open');
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.classList.remove('modal-open');
			document.body.style.overflow = '';
		};
	}, [open]);

	useEffect(() => {
		if (!open) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') onClose();
		};
		window.addEventListener('keydown', onKeyDown);
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [open, onClose]);

	const handleBackdropMouseDown = (event: ReactMouseEvent<HTMLDivElement>) => {
		if (event.target === event.currentTarget) onClose();
	};

	if (!open) return null;

	return (
		<>
			<div
				className="modal fade show d-block"
				role="dialog"
				tabIndex={-1}
				aria-modal="true"
				onMouseDown={handleBackdropMouseDown}
			>
				<div className="modal-dialog modal-lg modal-dialog-scrollable">
					<div className="modal-content">
						<div className="modal-header">
							<div>
								<h5 className="modal-title mb-0">{title}</h5>
								{subtitle ? <div className="text-muted small mt-1">{subtitle}</div> : null}
							</div>
							<button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
						</div>
						<div className="modal-body">
							{loading ? <div className="text-muted">Loading details…</div> : null}
							{!loading && error ? <div className="alert alert-warning py-2 mb-0">{error}</div> : null}
							{!loading && !error ? children : null}
						</div>
					</div>
				</div>
			</div>
			<div className="modal-backdrop fade show"></div>
		</>
	);
}
